import React, { useEffect, useMemo, useRef, useState } from "react";
import { TerminalSquare, ArrowRight } from "lucide-react";
import { useT } from "../context/LanguageContext";

const TerminalSection = () => {
  const t = useT();
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [lines, setLines] = useState([]);
  const [activeLine, setActiveLine] = useState(0);
  const [activeChar, setActiveChar] = useState(0);
  const script = useMemo(() => [
    { type: "prompt", text: `> ${t("terminal.tools.claude")} ${t("terminal.helper").toLowerCase()}` },
    { type: "out", text: "✓ agent created: policy_renewal_v1" },
    { type: "out", text: "✓ scenario generated: renewal + payment reminder" },
    { type: "out", text: "✓ connected: netgsm · webhook · crm_fetch" },
    { type: "blank" },
    { type: "prompt", text: "> fetch customer context from CRM before the call" },
    { type: "out", text: "✓ added step: pre_call_fetch" },
    { type: "out", text: "✓ connected: llm.context" },
    { type: "blank" },
    { type: "prompt", text: "> deploy the agent" },
    { type: "out", text: "✓ deployed: production · live" },
  ], [t]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && (setVisible(true), obs.disconnect()),
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Typewriter animation loop
  useEffect(() => {
    if (!visible) return;
    if (activeLine >= script.length) {
      // restart after pause
      const t = setTimeout(() => {
        setLines([]);
        setActiveLine(0);
        setActiveChar(0);
      }, 3500);
      return () => clearTimeout(t);
    }
    const current = script[activeLine];
    if (current.type === "blank") {
      const t = setTimeout(() => {
        setLines((l) => [...l, current]);
        setActiveLine((i) => i + 1);
        setActiveChar(0);
      }, 220);
      return () => clearTimeout(t);
    }
    if (activeChar < current.text.length) {
      const speed = current.type === "prompt" ? 28 : 14;
      const t = setTimeout(() => setActiveChar((c) => c + 1), speed);
      return () => clearTimeout(t);
    }
    const pause = current.type === "prompt" ? 550 : 200;
    const t = setTimeout(() => {
      setLines((l) => [...l, current]);
      setActiveLine((i) => i + 1);
      setActiveChar(0);
    }, pause);
    return () => clearTimeout(t);
  }, [visible, activeLine, activeChar, script]);

  const partial = activeLine < script.length ? script[activeLine] : null;
  const partialText = partial && partial.type !== "blank"
    ? partial.text.slice(0, activeChar)
    : "";

  return (
    <section
      id="build"
      ref={ref}
      className={`section-fade matrix-page ${visible ? "visible" : ""} py-20 px-6`}
      style={{ minHeight: "100vh", background: "var(--section-surface-2)" }}
    >
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center" style={{ minHeight: "calc(100vh - 10rem)" }}>
        {/* Copy */}
        <div>
          <h2
            className="font-bold tracking-tight"
            style={{
              color: "var(--fg)",
              fontSize: "clamp(2rem, 4.5vw, 3.25rem)",
            }}
          >
            <span style={{ color: "var(--fg)" }}>{t("terminal.title1")} </span>
            <span
              className="italic"
              style={{
                background:
                  "linear-gradient(90deg, var(--brand-coral), var(--brand-cyan))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              {t("terminal.title2")}
            </span>
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed" style={{ color: "var(--fg)", opacity: 0.92 }}>
            {t("terminal.subtitle")}
          </p>

          <div className="mt-7 flex flex-wrap gap-2">
            {[
              { label: t("terminal.tools.claude"), color: "#4B6FD4" },
              { label: t("terminal.tools.cursor"), color: "#FF7A7A" },
              { label: t("terminal.tools.codex"), color: "#14B8A6" },
              { label: t("terminal.tools.openclaw"), color: "#8B5CF6" },
              { label: t("terminal.tools.anyMcp"), color: "#2B50B8" },
            ].map((tool) => (
              <span
                key={tool.label}
                className="text-[12px] px-3 py-1.5 rounded-full"
                style={{
                  background: `${tool.color}14`,
                  border: `1px solid ${tool.color}35`,
                  color: tool.color,
                }}
              >
                {tool.label}
              </span>
            ))}
          </div>

          <p
            className="mt-7 text-[12px] uppercase tracking-[0.18em]"
            style={{ color: "var(--muted)" }}
          >
            {t("terminal.worksWith")}
          </p>
        </div>

        {/* Terminal */}
        <div className="matrix-terminal rounded-2xl overflow-hidden shadow-xl">
          {/* Terminal title bar */}
          <div className="matrix-terminal-bar flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#FF6B85" }} />
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#F5C04B" }} />
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#5EE0FF" }} />
            </div>
            <div className="matrix-terminal-label flex items-center gap-2 text-[10.5px] tracking-[0.18em] uppercase font-semibold">
              <TerminalSquare size={12} />
              claude code · mcp · verasist
            </div>
            <span className="w-2 h-2" />
          </div>

          {/* Terminal body */}
          <div
            className="matrix-terminal-body px-5 py-5 font-mono text-[13.5px] leading-[1.7] min-h-[360px]"
            style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace" }}
          >
            {lines.map((ln, i) => {
              if (ln.type === "blank") {
                return <div key={i} className="h-2" />;
              }
              const isPrompt = ln.type === "prompt";
              return (
                <div key={i} className="mb-1.5">
                  <span
                    style={{
                      color: isPrompt ? "var(--terminal-prompt)" : "var(--terminal-output)",
                    }}
                  >
                    {ln.text}
                  </span>
                </div>
              );
            })}

            {partial && partial.type !== "blank" && (
              <div>
                <span
                  style={{
                    color: partial.type === "prompt" ? "var(--terminal-prompt)" : "var(--terminal-output)",
                  }}
                >
                  {partialText}
                </span>
                <span
                  className="inline-block w-2 h-[14px] ml-0.5 align-middle"
                  style={{
                    background: "var(--terminal-prompt)",
                    animation: "termCursor 1s infinite",
                  }}
                />
                <style>{`@keyframes termCursor { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }`}</style>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Helper line below */}
      <p
        className="text-center mt-10 text-[13.5px]"
        style={{ color: "var(--muted)" }}
      >
        {t("terminal.helper")} <ArrowRight size={14} className="inline -mt-0.5" />
      </p>
    </section>
  );
};

export default TerminalSection;
